import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { formatarData, formatarDataHora, PERFIL_ROTULO, SENHA_MINIMA, type Perfil } from "@caixaflow/shared";
import { api, ErroApi, qs } from "../../api/cliente";
import { useSessao } from "../../api/sessao";
import type { Usuario } from "../../api/tipos";
import { Campo } from "../../components/Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "../../components/Estados";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";

type Acao = { tipo: "novo" } | { tipo: "editar"; usuario: Usuario } | { tipo: "senha"; usuario: Usuario };

export function PaginaUsuarios() {
  const [busca, setBusca] = useState("");
  const [acao, setAcao] = useState<Acao | null>(null);
  const { data: sessao } = useSessao();
  const qc = useQueryClient();
  const toast = useToast();

  const usuarios = useQuery({
    queryKey: ["usuarios", busca],
    queryFn: () => api<Usuario[]>(`/usuarios${qs({ busca: busca.trim() })}`),
    placeholderData: (anterior) => anterior,
  });

  const alternarAtivo = useMutation({
    mutationFn: (u: Usuario) => api<Usuario>(`/usuarios/${u.id}`, "PATCH", { ativo: !u.ativo }),
    onSuccess: (u) => {
      toast(u.ativo ? "Usuário reativado" : "Usuário desativado");
      qc.invalidateQueries({ queryKey: ["usuarios"] });
    },
    onError: (e) => toast(mensagemDeErro(e), "erro"),
  });

  return (
    <>
      <div className="linha">
        <div className="filtros" style={{ flex: 1 }}>
          <Campo rotulo="Buscar">
            <input type="search" placeholder="Nome ou e-mail" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </Campo>
        </div>
        <button className="btn primario" onClick={() => setAcao({ tipo: "novo" })}>
          Novo usuário
        </button>
      </div>

      {usuarios.isPending && <Carregando />}
      {usuarios.error && <FalhaAoCarregar erro={usuarios.error} tentarDeNovo={() => usuarios.refetch()} />}
      {usuarios.data && (
        <div className="card tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Perfil</th>
                <th>Situação</th>
                <th>Criado em</th>
                <th>Último acesso</th>
                <th className="r">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {usuarios.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="vazio">
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              )}
              {usuarios.data.map((u) => {
                const souEu = u.id === sessao?.usuario.id;
                return (
                  <tr key={u.id} className={u.ativo ? "" : "inativo"}>
                    <td>
                      {u.nome} {souEu && <span className="chip neutro">você</span>}
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`chip ${u.perfil === "ADMIN" ? "" : "neutro"}`}>{PERFIL_ROTULO[u.perfil]}</span>
                    </td>
                    <td>
                      {!u.ativo ? (
                        <span className="chip neutro">Inativo</span>
                      ) : u.deveTrocarSenha ? (
                        <span className="chip alerta">Senha provisória</span>
                      ) : (
                        <span className="chip ok">Ativo</span>
                      )}
                    </td>
                    <td className="mudo">{formatarData(u.criadoEm)}</td>
                    <td className="mudo">{u.ultimoAcessoEm ? formatarDataHora(u.ultimoAcessoEm) : "—"}</td>
                    <td>
                      <div className="acoes">
                        <button className="link-botao" onClick={() => setAcao({ tipo: "editar", usuario: u })}>
                          Editar
                        </button>
                        <button className="link-botao" onClick={() => setAcao({ tipo: "senha", usuario: u })}>
                          Redefinir senha
                        </button>
                        {!souEu && (
                          <button
                            className={`link-botao${u.ativo ? " perigo" : ""}`}
                            disabled={alternarAtivo.isPending}
                            onClick={() =>
                              (!u.ativo || window.confirm(`Desativar ${u.nome}? A sessão dele é encerrada na hora.`)) &&
                              alternarAtivo.mutate(u)
                            }
                          >
                            {u.ativo ? "Desativar" : "Reativar"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        aberto={acao !== null}
        titulo={acao?.tipo === "novo" ? "Novo usuário" : acao?.tipo === "senha" ? "Redefinir senha" : "Editar usuário"}
        onFechar={() => setAcao(null)}
      >
        {acao?.tipo === "senha" ? (
          <FormRedefinirSenha usuario={acao.usuario} onPronto={() => setAcao(null)} />
        ) : acao ? (
          <FormUsuario usuario={acao.tipo === "editar" ? acao.usuario : null} onPronto={() => setAcao(null)} />
        ) : null}
      </Modal>
    </>
  );
}

/** Senha provisória legível (sem caracteres que se confundem), para passar ao usuário. */
function gerarSenhaProvisoria(): string {
  const letras = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (b) => letras[b % letras.length]).join("");
}

function CampoSenhaProvisoria({ valor, onChange, erro }: { valor: string; onChange: (v: string) => void; erro?: string }) {
  return (
    <Campo rotulo="Senha provisória" dica={`mínimo ${SENHA_MINIMA} caracteres; o usuário troca no primeiro acesso`} erro={erro}>
      <div className="linha-esq" style={{ flexWrap: "nowrap" }}>
        <input required minLength={SENHA_MINIMA} value={valor} onChange={(e) => onChange(e.target.value)} autoComplete="off" />
        <button type="button" className="btn fantasma" onClick={() => onChange(gerarSenhaProvisoria())}>
          Gerar
        </button>
      </div>
    </Campo>
  );
}

function FormUsuario({ usuario, onPronto }: { usuario: Usuario | null; onPronto: () => void }) {
  const [nome, setNome] = useState(usuario?.nome ?? "");
  const [email, setEmail] = useState(usuario?.email ?? "");
  const [perfil, setPerfil] = useState<Perfil>(usuario?.perfil ?? "OPERADOR");
  const [senha, setSenha] = useState(() => (usuario ? "" : gerarSenhaProvisoria()));
  const qc = useQueryClient();
  const toast = useToast();

  const salvar = useMutation({
    mutationFn: () =>
      usuario
        ? api<Usuario>(`/usuarios/${usuario.id}`, "PATCH", { nome, email, perfil })
        : api<Usuario>("/usuarios", "POST", { nome, email, perfil, senhaTemporaria: senha }),
    onSuccess: () => {
      toast(usuario ? "Usuário atualizado" : "Usuário criado — informe a senha provisória a ele");
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      onPronto();
    },
  });
  const erro = salvar.error instanceof ErroApi ? salvar.error : null;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    salvar.mutate();
  };

  return (
    <form className="form" onSubmit={enviar}>
      {salvar.error && !erro?.campos.length && <div className="aviso erro">{mensagemDeErro(salvar.error)}</div>}
      <Campo rotulo="Nome" erro={erro?.doCampo("nome")}>
        <input required value={nome} onChange={(e) => setNome(e.target.value)} />
      </Campo>
      <Campo rotulo="E-mail (login)" erro={erro?.doCampo("email")}>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
      </Campo>
      <Campo
        rotulo="Perfil"
        dica={perfil === "ADMIN" ? "acesso total" : "abre o próprio caixa e registra vendas"}
      >
        <select value={perfil} onChange={(e) => setPerfil(e.target.value as Perfil)}>
          <option value="OPERADOR">Operador</option>
          <option value="ADMIN">Administrador</option>
        </select>
      </Campo>
      {!usuario && <CampoSenhaProvisoria valor={senha} onChange={setSenha} erro={erro?.doCampo("senhaTemporaria")} />}
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Cancelar
        </button>
        <button className="btn primario" type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </form>
  );
}

function FormRedefinirSenha({ usuario, onPronto }: { usuario: Usuario; onPronto: () => void }) {
  const [senha, setSenha] = useState(gerarSenhaProvisoria);
  const qc = useQueryClient();
  const toast = useToast();
  const redefinir = useMutation({
    mutationFn: () => api(`/usuarios/${usuario.id}/redefinir-senha`, "POST", { senhaTemporaria: senha }),
    onSuccess: () => {
      toast("Senha redefinida — informe a senha provisória ao usuário");
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      onPronto();
    },
  });
  const erro = redefinir.error instanceof ErroApi ? redefinir.error : null;

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        redefinir.mutate();
      }}
    >
      <p style={{ margin: 0 }}>
        <b>{usuario.nome}</b> será desconectado de todos os aparelhos e precisará trocar a senha no próximo acesso.
      </p>
      {redefinir.error && !erro?.campos.length && <div className="aviso erro">{mensagemDeErro(redefinir.error)}</div>}
      <CampoSenhaProvisoria valor={senha} onChange={setSenha} erro={erro?.doCampo("senhaTemporaria")} />
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Cancelar
        </button>
        <button className="btn primario" type="submit" disabled={redefinir.isPending}>
          {redefinir.isPending ? "Salvando…" : "Redefinir"}
        </button>
      </div>
    </form>
  );
}
